/**
 * Lines in a template spec: the fields of each line of an entry, held inside
 * the `lines` field that owns them. Decisions 2 and 3 of
 * docs/plans/entry-lines-and-layout.md.
 *
 * A lines record is never a record of the spec's own list. It belongs to one
 * field, so it is written inside that field (`SpecField.lines`), and
 * every screen that lists a spec's records (the setup proposal, the outline)
 * keeps listing only the records a person writes into. Apply makes it a row of
 * `app.record` of kind `lines`, with the two reference fields every lines
 * record holds and nobody edits: `parent`, the row the line hangs from, and
 * `root`, the entry at the top.
 *
 * `linesRecordsOf` writes each lines record out as a record of the spec would
 * be written, those two fields and their bindings included, so the validator
 * holds a line to every rule a record's field is held to and apply inserts it
 * through the same loop. One expansion, read by both.
 */
import type { RecordKind } from '../api';
import { PRIMITIVE_FOR } from '../generated/value-types';
import type { SpecField, SpecLines, SpecRecord } from './spec';

/** The key a line formula reads the row above through. */
export const LINES_PARENT_KEY = 'parent';

/** The key a line formula reads the entry at the top through. */
export const LINES_ROOT_KEY = 'root';

/** The catalogue words of the two fields: the server's planner says the same. */
const PARENT_LABEL_KEY = 'field.lines_parent';
const ROOT_LABEL_KEY = 'field.lines_root';

/** A record of the spec, or a lines record written out as one. */
export type SpecRecordOrLines = Omit<SpecRecord, 'kind'> & { kind: RecordKind };

/** One lines record, written out with its parent and root fields and bindings. */
export type LinesRecordSpec = Omit<SpecRecord, 'kind'> & {
  kind: 'lines';
  /** The record (or lines record) the `lines` field sits on. */
  parentKey: string;
  /** The record at the top of the tree. */
  rootKey: string;
  /** 1 under a record, 2 under a line. */
  depth: number;
  /** The `lines` field that owns it, on the parent. */
  fieldKey: string;
  lines: SpecLines;
  /** Where a problem with it is reported: under the field that owns it. */
  path: string;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The parent or root field of a lines record: a reference nobody edits, on r1 or r2. */
const serverReference = (key: string, labelKey: string, targetRecordKey: string, ord: number): SpecField => ({
  key,
  labelKey,
  type: 'reference',
  tier: 'primary',
  component: PRIMITIVE_FOR.reference,
  props: {},
  storage: 'slot',
  slot: `r${ord}`,
  targetRecordKey,
  ord,
});

/**
 * Every lines record the records hold, parents before children, each written
 * out as a record. Read defensively: a spec is JSON on disk, and a `lines`
 * block that is not an object with a key and a list of fields is left for the
 * validator to refuse by name (`LINES_SHAPE`), never thrown on here.
 *
 * A lines record under a lines record is written out too, at any depth, so
 * the validator can refuse the third level (`LINES_TOO_DEEP`) rather than
 * never see it.
 */
export const linesRecordsOf = (records: readonly unknown[], prefix = ''): LinesRecordSpec[] => {
  const out: LinesRecordSpec[] = [];
  const walk = (owner: { key: string; fields: unknown }, rootKey: string, depth: number, at: string): void => {
    for (const field of Array.isArray(owner.fields) ? owner.fields : []) {
      if (!isObject(field) || field.type !== 'lines' || !isObject(field.lines)) continue;
      const lines = field.lines as SpecLines;
      if (typeof lines.key !== 'string' || !Array.isArray(lines.fields)) continue;
      const path = `${at}.fields[${String(field.key)}].lines`;
      const record: LinesRecordSpec = {
        key: lines.key,
        kind: 'lines',
        nameKey: lines.nameKey,
        fields: [
          serverReference(LINES_PARENT_KEY, PARENT_LABEL_KEY, owner.key, 1),
          serverReference(LINES_ROOT_KEY, ROOT_LABEL_KEY, rootKey, 2),
          ...lines.fields,
        ],
        bindings: [
          { purpose: 'parent', fieldKey: LINES_PARENT_KEY },
          { purpose: 'root', fieldKey: LINES_ROOT_KEY },
        ],
        views: [],
        parentKey: owner.key,
        rootKey,
        depth: depth + 1,
        fieldKey: String(field.key),
        lines,
        path,
      };
      out.push(record);
      walk(record, rootKey, depth + 1, path);
    }
  };
  for (const record of records) {
    if (!isObject(record) || typeof record.key !== 'string') continue;
    walk({ key: record.key, fields: record.fields }, record.key, 0, `${prefix}records[${record.key}]`);
  }
  return out;
};
